/**
 * 任务委托状态机 API（对齐后端 Task 2.1 / design.md D3）
 */
import { request } from '@/shared/api/request'
import type { AxiosRequestConfig } from 'axios'
import type { BatchRequestConfig } from '@/shared/api/use-api-batch'
import {
  TaskAssignmentType,
  TaskStatus,
  ProjectType,
  TaskPurpose,
  TaskType,
} from '@/api/common-types'
import type { PaginationParams, PaginatedResponse, UserSummary } from '@/api/common-types'
import { assignReviewTask, getWorkItem } from '@/modules/project/api/work-item'

type RequestConfig = AxiosRequestConfig | BatchRequestConfig

export { TaskAssignmentType, TaskPurpose, TaskType, TaskStatus, ProjectType }

export interface TaskReviewRecord {
  id: number
  task_id: number
  round: number
  comment: string
  created_at?: string | null
}

/** 任务响应（新状态机） */
export interface TaskResponse {
  id: number
  work_item_id: number
  task_purpose: TaskPurpose | string
  creator_id: number
  assignee_id?: number | null
  status: TaskStatus | string
  round: number
  rejection_reason?: string | null
  created_at?: string | null
  started_at?: string | null
  submitted_at?: string | null
  completed_at?: string | null
  review_records?: TaskReviewRecord[]
  /** 过渡期：列表/通知 UI 可能附带用户摘要 */
  creator?: UserSummary | null
  assignee?: UserSummary | null
  /** 过渡期：旧任务 UI 字段 */
  deadline?: string | null
  /** 过渡期兼容字段（旧列表 UI） */
  project_id?: number
  project_type?: ProjectType
  project_name?: string
  title?: string
  description?: string
  task_type?: string
}

export interface TaskListResponse extends PaginatedResponse<TaskResponse> {
  items: TaskResponse[]
}

export interface TaskQueryParams extends PaginationParams {
  status?: TaskStatus | string
  work_item_id?: number
  /** @deprecated */
  project_id?: number
  /** @deprecated */
  project_type?: ProjectType
  /** @deprecated */
  task_type?: TaskType
  search?: string
  keyword?: string
  sort_by?: string
  sort_order?: 'asc' | 'desc'
}

/** 任务指派弹窗：可选被指派人列表（来自 /tasks/assignee-options） */
export interface TaskSelectionOptions {
  same_group_users: UserSummary[]
  metalingual_users: UserSummary[]
  can_assign_to_group?: boolean
  can_assign_to_metalingual?: boolean
}

/** 任务指派请求（目标语言在条目创建时已确定） */
export interface TaskAssignmentCreate {
  assignee_id: number
  /** @deprecated UI 遗留字段，后端不再接收 */
  task_type?: TaskType | string
  description?: string
  target_languages?: string[]
}

function deprecatedTaskApi(name: string): never {
  throw new Error(
    `${name} 已废弃：请使用 accept/reject/submit/revise/approve 与 received/assigned 列表。`,
  )
}

export async function getTask(
  id: number,
  config?: RequestConfig,
): Promise<TaskResponse> {
  return request.get<TaskResponse>(`/tasks/${id}`, config)
}

export async function getReceivedTasks(
  params?: TaskQueryParams,
  config?: RequestConfig,
): Promise<TaskListResponse> {
  return request.get<TaskListResponse>('/tasks/received', { params, ...config })
}

export async function getAssignedTasks(
  params?: TaskQueryParams,
  config?: RequestConfig,
): Promise<TaskListResponse> {
  return request.get<TaskListResponse>('/tasks/assigned', { params, ...config })
}

export async function getTaskReviewRecords(
  taskId: number,
  config?: RequestConfig,
): Promise<TaskReviewRecord[]> {
  return request.get<TaskReviewRecord[]>(`/tasks/${taskId}/review-records`, config)
}

export async function acceptTask(id: number): Promise<TaskResponse> {
  return request.post<TaskResponse>(`/tasks/${id}/accept`)
}

export async function rejectTask(id: number, reason: string): Promise<TaskResponse> {
  return request.post<TaskResponse>(`/tasks/${id}/reject`, { reason })
}

export async function submitTask(id: number): Promise<TaskResponse> {
  return request.post<TaskResponse>(`/tasks/${id}/submit`)
}

export async function reviseTask(id: number, comment: string): Promise<TaskResponse> {
  return request.post<TaskResponse>(`/tasks/${id}/revise`, { comment })
}

/** 审核通过（完成 Task + WorkItem + 自动报告） */
export async function approveTask(id: number): Promise<TaskResponse> {
  return request.post<TaskResponse>(`/tasks/${id}/approve`)
}

/**
 * 状态机操作后拉取最新 WorkItem（approve/reject/submit 等副作用落在条目上）。
 * UI 可在操作成功后调用以刷新工作区按钮。
 */
export async function refreshWorkItemAfterTaskAction(
  workItemId: number,
): Promise<Awaited<ReturnType<typeof getWorkItem>>> {
  return getWorkItem(workItemId)
}

/** @deprecated 请使用 approveTask */
export async function completeTask(id: number, completionMessage?: string): Promise<TaskResponse> {
  void completionMessage
  return approveTask(id)
}

/** 客户端聚合统计（后端已无 /stats） */
export function aggregateTaskStatusCounts(
  items: TaskResponse[],
): Record<string, number> {
  const counts: Record<string, number> = {
    total: items.length,
    pending: 0,
    processing: 0,
    submitted: 0,
    completed: 0,
    rejected_at_assignment: 0,
  }
  for (const item of items) {
    const key = String(item.status || 'unknown')
    counts[key] = (counts[key] ?? 0) + 1
  }
  return counts
}

/** @deprecated */
/* eslint-disable @typescript-eslint/no-unused-vars -- 废弃签名保留参数名供调用方对照 */
export async function getTasks(_params?: TaskQueryParams): Promise<TaskListResponse> {
  deprecatedTaskApi('getTasks')
}

/** @deprecated */
export async function updateTaskStatus(
  _id: number,
  _data: { status: TaskStatus },
): Promise<TaskResponse> {
  deprecatedTaskApi('updateTaskStatus')
}

/** @deprecated */
export async function getTaskByProject(
  _projectId: number,
  _projectType: ProjectType,
): Promise<TaskResponse> {
  deprecatedTaskApi('getTaskByProject')
}

/** @deprecated */
export async function getTaskProgressSummary(): Promise<never> {
  deprecatedTaskApi('getTaskProgressSummary')
}

/** @deprecated */
export async function getProjectTaskProgress(
  _projectId: number,
  _projectType: ProjectType,
  _config?: RequestConfig,
): Promise<never> {
  deprecatedTaskApi('getProjectTaskProgress')
}

/** @deprecated */
export async function reassignTask(_taskId: number, _data: { new_assignee_id: number }): Promise<never> {
  deprecatedTaskApi('reassignTask')
}

/** @deprecated */
export async function cancelTask(_taskId: number, _data?: { reason?: string }): Promise<never> {
  deprecatedTaskApi('cancelTask')
}

/** @deprecated */
export async function createTask(_data: unknown): Promise<never> {
  deprecatedTaskApi('createTask')
}

/** @deprecated */
export async function deleteTask(_id: number): Promise<never> {
  deprecatedTaskApi('deleteTask')
}

/** @deprecated */
export async function startTask(_id: number): Promise<never> {
  deprecatedTaskApi('startTask')
}

/** @deprecated */
export async function updateTaskProgress(_id: number, _progress: number): Promise<never> {
  deprecatedTaskApi('updateTaskProgress')
}

/** @deprecated */
export async function batchUpdateTaskStatus(_ids: number[], _status: string): Promise<never> {
  deprecatedTaskApi('batchUpdateTaskStatus')
}

/** @deprecated */
export async function batchDeleteTasks(_ids: number[]): Promise<never> {
  deprecatedTaskApi('batchDeleteTasks')
}

/** @deprecated 请用 aggregateTaskStatusCounts(getAssignedTasks) */
export async function getTaskStats(_params?: unknown): Promise<Record<string, number>> {
  const list = await getAssignedTasks({ page: 1, page_size: 100 })
  return aggregateTaskStatusCounts(list.items ?? [])
}

/** @deprecated 请用 aggregateTaskStatusCounts(getReceivedTasks) */
export async function getReceivedTaskStats(_params?: unknown): Promise<Record<string, number>> {
  const list = await getReceivedTasks({ page: 1, page_size: 100 })
  return aggregateTaskStatusCounts(list.items ?? [])
}

/** @deprecated */
export async function getMyTasks(params?: TaskQueryParams): Promise<TaskListResponse> {
  return getReceivedTasks(params)
}

/** @deprecated */
export async function getOverdueTasks(_params?: TaskQueryParams): Promise<never> {
  deprecatedTaskApi('getOverdueTasks')
}

export async function getTaskSelectionOptions(
  _workItemId?: number,
  _projectType?: ProjectType,
  config?: RequestConfig,
): Promise<TaskSelectionOptions> {
  void _workItemId
  void _projectType
  return request.get<TaskSelectionOptions>('/tasks/assignee-options', config)
}

function assignmentResultToTaskResponse(
  workItemId: number,
  taskId: number,
): TaskResponse {
  return {
    id: taskId,
    work_item_id: workItemId,
    task_purpose: 'translate',
    creator_id: 0,
    status: 'pending',
    round: 0,
  }
}

export async function assignVideoTask(
  workItemId: number,
  data: TaskAssignmentCreate,
): Promise<TaskResponse> {
  const result = await assignReviewTask(workItemId, { assignee_id: data.assignee_id })
  return assignmentResultToTaskResponse(result.work_item_id, result.task_id)
}

export async function assignDocumentTask(
  workItemId: number,
  data: TaskAssignmentCreate,
): Promise<TaskResponse> {
  const result = await assignReviewTask(workItemId, { assignee_id: data.assignee_id })
  return assignmentResultToTaskResponse(result.work_item_id, result.task_id)
}
/* eslint-enable @typescript-eslint/no-unused-vars */

export const taskApi = {
  getTask,
  getReceivedTasks,
  getAssignedTasks,
  getTaskReviewRecords,
  acceptTask,
  rejectTask,
  submitTask,
  reviseTask,
  approveTask,
  refreshWorkItemAfterTaskAction,
  completeTask,
  aggregateTaskStatusCounts,
  getTasks,
  updateTaskStatus,
  getTaskByProject,
  getTaskProgressSummary,
  getProjectTaskProgress,
  reassignTask,
  cancelTask,
  createTask,
  getTaskById: getTask,
  deleteTask,
  startTask,
  updateTaskProgress,
  batchUpdateTaskStatus,
  batchDeleteTasks,
  getTaskStats,
  getReceivedTaskStats,
  getMyTasks,
  getOverdueTasks,
  getTaskSelectionOptions,
  assignVideoTask,
  assignDocumentTask,
}

export default taskApi
